import { useMemo } from 'react';

import { isPartialPolicyPayload, TypedPolicy } from '../../../types';

import {
  KUBERNETES_SECURITY_FORM_INITIAL_VALUES,
  KUBERNETES_SECURITY_INITIAL_VALUES,
  KubernetesSecurityFormValues,
  KubernetesSecurityPolicyPayload,
  KubernetesSecurityTypeSpecificData,
} from './types';

/**
 * Type guard to check if data is KubernetesSecurityPolicyPayload
 */
function isKubernetesSecurityPolicyPayload(
  data: unknown
): data is KubernetesSecurityPolicyPayload {
  return (
    typeof data === 'object' &&
    data !== null &&
    'restrictDefaultNamespace' in data
  );
}

/**
 * Deserializes a Kubernetes Security policy payload into form values.
 * Used by both edit mode (loading existing policy) and template mode (applying template data).
 *
 * @param data - The policy payload data to deserialize
 * @param defaults - Default values to use when payload fields are missing
 * @returns Type-specific form values
 */
function deserializePayload(
  data: Partial<KubernetesSecurityPolicyPayload>,
  defaults: KubernetesSecurityTypeSpecificData
): KubernetesSecurityTypeSpecificData {
  return {
    restrictDefaultNamespace:
      data.restrictDefaultNamespace?.value ?? defaults.restrictDefaultNamespace,
    restrictSecrets: data.restrictSecrets?.value ?? defaults.restrictSecrets,
    enableNodeShell: data.enableNodeShell?.value ?? defaults.enableNodeShell,
    enablePodSecurityConstraints:
      data.enablePodSecurityConstraints?.value ??
      defaults.enablePodSecurityConstraints,
    // Match the same polarity as buildKubernetesSecurityPayload:
    // privilegedContainers.value.enabled === true means we restrict privileged containers.
    restrictPrivilegedContainers:
      data.privilegedContainers?.value?.enabled !== undefined
        ? data.privilegedContainers.value.enabled
        : defaults.restrictPrivilegedContainers,
    restrictHostNamespace:
      data.hostNamespaces?.value?.enabled ?? defaults.restrictHostNamespace,
    restrictHostNetworkingPorts:
      data.hostPorts?.value?.enabled ?? defaults.restrictHostNetworkingPorts,
    hostNetwork: data.hostPorts?.value?.hostNetwork ?? defaults.hostNetwork,
    hostPortMin: data.hostPorts?.value?.min ?? defaults.hostPortMin,
    hostPortMax: data.hostPorts?.value?.max ?? defaults.hostPortMax,
    restrictVolumeTypes:
      data.volumeTypes?.value?.enabled ?? defaults.restrictVolumeTypes,
    allowedVolumeTypes:
      data.volumeTypes?.value?.allowedTypes ?? defaults.allowedVolumeTypes,
    restrictHostFilesystemPaths:
      data.hostFilesystem?.value?.enabled ??
      defaults.restrictHostFilesystemPaths,
    allowedHostPaths:
      data.hostFilesystem?.value?.allowedPaths ?? defaults.allowedHostPaths,
    restrictDrivers:
      data.allowFlexVolumes?.value?.enabled ?? defaults.restrictDrivers,
    allowedFlexVolumeDrivers:
      data.allowFlexVolumes?.value?.allowedVolumes ??
      defaults.allowedFlexVolumeDrivers,
    requireReadOnlyRootFilesystem:
      data.readOnlyRootFileSystem?.value?.enabled ??
      defaults.requireReadOnlyRootFilesystem,
    restrictUserAndGroupIds:
      data.users?.value?.enabled ?? defaults.restrictUserAndGroupIds,
    runAsUserType: data.users?.value?.runAsUser?.type ?? defaults.runAsUserType,
    runAsUserRanges:
      data.users?.value?.runAsUser?.idrange ?? defaults.runAsUserRanges,
    runAsGroupType:
      data.users?.value?.runAsGroup?.type ?? defaults.runAsGroupType,
    runAsGroupRanges:
      data.users?.value?.runAsGroup?.idrange ?? defaults.runAsGroupRanges,
    supplementalGroupsType:
      data.users?.value?.supplementalGroups?.type ??
      defaults.supplementalGroupsType,
    supplementalGroupsRanges:
      data.users?.value?.supplementalGroups?.idrange ??
      defaults.supplementalGroupsRanges,
    fsGroupType: data.users?.value?.fsGroups?.type ?? defaults.fsGroupType,
    fsGroupRanges:
      data.users?.value?.fsGroups?.idrange ?? defaults.fsGroupRanges,
    restrictEscalationToRootPrivileges:
      data.allowPrivilegeEscalation?.value?.enabled ??
      defaults.restrictEscalationToRootPrivileges,
    restrictLinuxCapabilities:
      data.capabilities?.value?.enabled ?? defaults.restrictLinuxCapabilities,
    allowedCapabilities:
      data.capabilities?.value?.allowedCapabilities ??
      defaults.allowedCapabilities,
    requiredDropCapabilities:
      data.capabilities?.value?.requiredDropCapabilities ??
      defaults.requiredDropCapabilities,
    defaultAddCapabilities: defaults.defaultAddCapabilities,
    restrictSeLinuxSecurityContext:
      data.selinux?.value?.enabled ?? defaults.restrictSeLinuxSecurityContext,
    seLinuxOptions:
      data.selinux?.value?.allowedCapabilities ?? defaults.seLinuxOptions,
    restrictProcMountTypes:
      data.allowProcMount?.value?.enabled ?? defaults.restrictProcMountTypes,
    procMountType:
      data.allowProcMount?.value?.procMountType ?? defaults.procMountType,
    restrictAppArmorProfiles:
      data.appArmor?.value?.enabled ?? defaults.restrictAppArmorProfiles,
    allowedAppArmorProfiles:
      data.appArmor?.value?.AppArmorType ?? defaults.allowedAppArmorProfiles,
    restrictSeccompProfiles:
      data.secComp?.value?.enabled ?? defaults.restrictSeccompProfiles,
    allowedSeccompProfiles:
      data.secComp?.value?.secCompType ?? defaults.allowedSeccompProfiles,
    restrictSysctlProfiles:
      data.forbiddenSysctlsList?.value?.enabled ??
      defaults.restrictSysctlProfiles,
    forbiddenSysctls:
      data.forbiddenSysctlsList?.value?.requiredDropCapabilities ??
      defaults.forbiddenSysctls,
  };
}

/**
 * Hook to get initial values for Kubernetes Security Policy form.
 *
 * Returns complete form values including common fields (name, environmentGroupIds)
 * and type-specific fields.
 *
 * @param policy - Optional policy to load values from (for edit mode)
 * @param templateData - Optional template data to pre-fill form (for create from template)
 * @returns Complete form initial values
 */
export function useKubernetesSecurityInitialValues(
  policy?: TypedPolicy<unknown>,
  templateData?: Record<string, unknown>
): KubernetesSecurityFormValues {
  return useMemo(() => {
    const defaults = KUBERNETES_SECURITY_INITIAL_VALUES;

    // Template mode: apply template data over defaults
    if (
      !policy &&
      templateData &&
      isPartialPolicyPayload<KubernetesSecurityPolicyPayload>(templateData)
    ) {
      return {
        name: '',
        environmentGroupIds: [],
        ...deserializePayload(templateData, defaults),
      };
    }

    // Create mode: use defaults
    if (!policy) {
      return KUBERNETES_SECURITY_FORM_INITIAL_VALUES;
    }

    // Edit mode: deserialize from policy data
    const data = policy.Data;
    if (!isKubernetesSecurityPolicyPayload(data)) {
      return {
        name: policy.Name,
        environmentGroupIds: policy.EnvironmentGroups ?? [],
        ...defaults,
      };
    }

    return {
      name: policy.Name,
      environmentGroupIds: policy.EnvironmentGroups ?? [],
      ...deserializePayload(data, defaults),
    };
  }, [policy, templateData]);
}
